/* OcuLabs cart drawer for every page (after PetLabCo's). Reads and writes the real Shopify cart through the Ajax API.
   It began as the Vision cat-layout page's drawer; Jonathan, 28 Oct 2026, made it global. Every layout renders the
   theme's cart-drawer section, which renders this drawer; when it is on, it takes over the Shrine drawer's open() and
   renderContents(), so theme forms, GemPages buttons, Kaching widgets and the header icon all open this one.
   Vision, Calming and Hip + Joint packs are Kaching bundles: the jar stepper swaps the whole pack (1, 2 or 3 jars, same
   purchase mode). Vision goes through the cat-layout page's builder (window.ocVision) on that page and this drawer's
   copy of it everywhere else, and that page's free gifts follow its packs; Calming and Hip go through their deals'
   bars (packDeals), Hip's free jars and guide included. Other lines change quantity, as the theme drawer did. */
(() => {
  'use strict';
  const root = document.getElementById('ocv-cart');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = '1';
  const data = JSON.parse(root.querySelector('[data-ocv-cart-data]').textContent);

  // The switch: ?oc_cart=1 turns this drawer on for one browser (QA), ?oc_cart=0 turns it off for one browser.
  const SWITCH = 'oc_cart';
  let stored = null;
  try {
    const asked = new URLSearchParams(location.search).get(SWITCH);
    if (asked === '1' || asked === '0') localStorage.setItem(SWITCH, asked);
    stored = localStorage.getItem(SWITCH);
  } catch {}
  const active = data.always || (stored === '0' ? false : data.live || stored === '1');
  if (!active) { root.remove(); return; }
  for (const href of [data.css, data.font]) {
    if (!href || document.querySelector(`link[href="${href}"]`)) continue;
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = href;
    document.head.append(link);
  }

  const panel = root.querySelector('.ocv-cart__panel');
  const body = root.querySelector('[data-ocv-body]');
  const head = root.querySelector('.ocv-cart__head');
  const count = root.querySelector('[data-ocv-count]');
  const total = root.querySelector('[data-ocv-total]');
  const checkout = root.querySelector('[data-ocv-checkout]');
  const status = root.querySelector('[data-ocv-status]');
  const base = data.root.replace(/\/$/, '');
  const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: data.currency});
  const money = cents => fmt.format(cents / 100);
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const icons = {
    trash: '<svg viewBox="0 0 24 24"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"/></svg>',
    minus: '<svg viewBox="0 0 24 24"><path d="M5 12h14"/></svg>',
    plus: '<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>',
    tick: '<svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
    bolt: '<svg viewBox="0 0 24 24"><path d="M13 2 4 14h7l-1 8 9-12h-7z"/></svg>',
    // Cart with a plus in the basket and solid wheels (his ask, 27 Oct 2026: "upgrade the cart icon").
    cart: '<svg viewBox="0 0 24 24"><path d="M2 3.5h2.3l2.4 11a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 1.95-1.55L21 7.2H5.4"/><path d="M13.2 8.7v4.4M11 10.9h4.4"/><circle cx="9.2" cy="20" r="1.5"/><circle cx="17.2" cy="20" r="1.5"/></svg>',
    chevron: '<svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>',
    bag: '<svg viewBox="0 0 24 24"><path d="M5 7h14l1 14H4L5 7Z"/><path d="M8 8V6a4 4 0 0 1 8 0v2"/></svg>',
    truck: '<svg viewBox="0 0 24 24"><path d="M3 6h11v9H3zM14 9h4l3 3v3h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>',
    checkCircle: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M7.5 12.3l3 3 6-6.3"/></svg>'
  };
  let cart = null, isOpen = false, busy = false, lastFocus = null, seen = new Set(), lastJars = 0, headerCount = null;
  let checkingOut = false, checkoutError = null, taurineCartSeen = !!window.OcuLabsTaurineNativeConfig;
  const expanded = new Set();

  const wait = ms => new Promise(done => setTimeout(done, ms));
  const request = async (path, payload, tries = 3) => {
    const response = await fetch(base + path, payload
      ? {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(payload)}
      : {headers: {Accept: 'application/json'}, cache: 'no-store'});
    // Shopify answers 429 without doing anything when the cart is hit too fast (seen 28 Oct 2026), so wait and resend.
    if (response.status === 429 && tries > 1) { await wait(1200 * (4 - tries)); return request(path, payload, tries - 1); }
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json.status) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
    return json;
  };
  const loadCart = () => request('/cart.js');
  // add.js answers with only the lines it added, also under `items`, so a whole cart is told apart by its totals: taking
  // that answer as the cart showed just the add-on, dropped the pack from view and made the total A$NaN (his recording
  // on the cat page, 28 Oct 2026).
  const isCart = r => Array.isArray(r?.items) && typeof r.total_price === 'number' && typeof r.item_count === 'number';

  // Kaching marks its bundle lines with a JSON property: {id, deal, main} on the paid jars, {bxgy: true} on the free
  // "get" jars of a Buy X, Get Y bar and {gift} on its free gift products (seen on Hip + Joint, 28 Oct 2026).
  const kaching = l => { try { return JSON.parse(l.properties?.__kaching_bundles || 'null') || {}; } catch { return {}; } };
  // A gift is a line marked as one by us or by Kaching, or any line the cart makes free. Only these exact markers:
  // paid lines can carry gift notes too (the cat page's `_oc_gifts`, 28 Oct 2026), and they are not gifts.
  // The dental page marks its free guide and surprise lines `_oc_dental_role: gift` (28 Oct 2026).
  const giftMarked = l => !!(l.properties?._oc_vision_gift || l.properties?._gift === 'true' || kaching(l).gift
    || l.properties?._oc_dental_role === 'gift' || l.properties?._oc_taurine_gift === 'cat-care' || l.properties?._oc_free_gift || l.properties?._oc_catvar_gift || l.properties?._oc_catvar_guide);
  const isGift = l => giftMarked(l) || l.final_line_price === 0;
  const isFreeUnit = l => !!kaching(l).bxgy && l.final_line_price === 0;

  // Free gifts for carts from ONE hidden page only (1 Nov 2026 correction, Jonathan: "make hidden versons like calming
  // and vision"; the first version had put the silvervine sticks on every dental order). `freeGifts`
  // (snippets/oc-cart.liquid) names the gift variant, the products it comes with and the line properties a paid line of
  // them must carry. Only /pages/cat-dental-care?view=oc-dental-save-silvervine stamps `_oc_gift_page: silvervine` on its
  // tub lines, so the main dental arms never qualify. One $0 gift line while such a line is in the cart, none otherwise
  // (a stray gift line is taken out). A gift Shopify prices above $0 is taken out and not tried again on this page, so it
  // never charges. Kept in step after every change this drawer makes (pruneGifts) and on page load when the server saw
  // the cart out of step. A gift entry without lineProps is ignored, so this can never apply store-wide.
  const freeGifts = (data.freeGifts || []).filter(g => g.variantId && g.productIds?.length && g.lineProps && Object.keys(g.lineProps).length);
  const freeGiftOf = l => freeGifts.find(g => g.variantId === l.variant_id);
  const freeGiftBlocked = new Set();
  const freeGiftWanted = (g, c) => !freeGiftBlocked.has(g.key) && c.items.some(l => g.productIds.includes(l.product_id)
    && !freeGiftOf(l) && !giftMarked(l) && l.final_line_price > 0
    && Object.entries(g.lineProps).every(([k, v]) => l.properties?.[k] === v));
  function freeGiftChanges(c) {
    const updates = {}, adds = [];
    for (const g of freeGifts) {
      const want = freeGiftWanted(g, c);
      let kept = false;
      for (const l of c.items.filter(l => l.variant_id === g.variantId)) {
        const keep = want && !kept && l.properties?._oc_free_gift === g.key && !l.selling_plan_allocation && l.final_line_price === 0;
        if (keep) { kept = true; if (l.quantity !== 1) updates[l.key] = 1; } else updates[l.key] = 0;
      }
      if (want && !kept) adds.push({id: g.variantId, quantity: 1, properties: {...g.props}});
    }
    return {updates, adds, any: Object.keys(updates).length > 0 || adds.length > 0};
  }
  async function syncFreeGifts(c) {
    if (!freeGifts.length || !isCart(c)) return c;
    for (let guard = 0; guard < 3; guard++) {
      const {updates, adds, any} = freeGiftChanges(c);
      if (!any) return c;
      if (Object.keys(updates).length) c = await request('/cart/update.js', {updates});
      if (!adds.length) continue;
      const added = (await request('/cart/add.js', {items: adds})).items || [];
      added.filter(l => freeGiftOf(l) && l.final_line_price > 0).forEach(l => freeGiftBlocked.add(freeGiftOf(l).key));
      c = await loadCart();
    }
    return c;
  }
  const isVision = l => data.visionVariantIds.includes(l.variant_id) && !isGift(l);
  const isPack = l => isVision(l) && !!l.properties?.__kaching_bundles;
  // A dental pack and its gifts share `_oc_dental_request`, so removing the pack takes its gifts with it.
  const group = l => l.properties?._oc_catvar_group || l.properties?._oc_taurine_group || l.properties?._oc_vision_group || l.properties?._oc_pack_group || l.properties?._oc_dental_request;
  // The lines that stand or fall together: our pack group, or one Kaching bundle (paid jars, free jars, gifts).
  const bundleKey = l => group(l) || kaching(l).id || null;
  // The Vision cat-layout page has its own pack builder (window.ocVision). Only that page gives the guide and Mystery
  // Gift, so the bar's gift tiers and the upgrade card show there only.
  const onVisionPage = () => typeof window.ocVision?.bundleItems === 'function';
  // Everywhere else a Vision pack swaps through this copy of that builder (his ask, 28 Oct 2026: the homepage cart
  // showed a 2-jar pack with only a bin). It takes packs of the same Kaching deal and version, which the original
  // page's widget writes too; the guide and Mystery Gift follow only a pack the cat-layout page sold.
  const vp = data.visionPack || {deal: {bars: {}}, plans: [], gifts: []};
  const rate = data.currency === data.baseCurrency ? 1
    : Number(window.Shopify?.currency?.active === data.currency && window.Shopify.currency.rate);
  const planFor = jars => vp.plans.find(p => p.id === vp.deal.bars[jars]?.plan);
  const ownBuilder = {
    bundleItems(jars, subscribe, base = {}) {
      const bar = vp.deal.bars[jars], p = subscribe ? planFor(jars) : null;
      if (!bar || (subscribe && !p)) return null;
      const id = window.crypto?.randomUUID?.() || Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      const token = {deal: vp.deal.nanoId, pp: vp.productId, main: true, v: vp.deal.versionId, bid: bar.id};
      if (p) token.sellingPlan = {id: p.id, priceAdjustment: {type: 'percentage', value: p.adjustment}};
      const items = [{id: vp.variantId, quantity: jars, properties: {...base, _oc_vision_group: id, __kaching_bundles: JSON.stringify(token)}}];
      if (p) items[0].selling_plan = p.id;
      // 29 Oct 2026: two cat-layout versions, each with its own gifts (the original-gifts one replaced the GemPages page).
      const giftSet = base._oc_vision_page === 'vision-hw-v1' ? vp.gifts : base._oc_vision_page === 'vision-hw-v1-og' ? vp.giftsOg || [] : [];
      giftSet.filter(g => jars >= g.min).forEach(g =>
        items.push({id: g.variantId, quantity: 1, properties: {...(g.props || {}), _gift: 'true', _oc_vision_gift: g.key, _oc_vision_group: id}}));
      return items;
    },
    // Kaching prices a pack at its bar's amount (shop currency, at the market rate), less the plan's percentage.
    price(jars, subscribe) {
      const bar = vp.deal.bars[jars];
      if (!bar || !(rate > 0)) return null;
      const once = Math.round(bar.value * rate * 100);
      return subscribe ? Math.round(once * (1 - (planFor(jars)?.adjustment || 0) / 100)) : once;
    }
  };
  const builder = () => onVisionPage() ? window.ocVision : ownBuilder;
  const canSwap = l => onVisionPage()
    || (l.variant_id === vp.variantId && kaching(l).deal === vp.deal.nanoId && kaching(l).v === vp.deal.versionId);

  // Calming and Hip + Joint packs step the same way, through their own Kaching deals (his ask, 28 Oct 2026: give them
  // the stepper too). `packDeals` holds each deal's bars by jar count; Hip's 2 and 3 jars bring free jars and the guide.
  const tp=data.taurinePacks||{};
  const isTaurine=l=>!isGift(l)&&Object.values(tp).some(p=>p.id===Number(l.variant_id));
  const taurineCount=l=>l.quantity*(Number(Object.entries(tp).find(([,p])=>p.id===Number(l.variant_id))?.[0])||1);
  // Calming, omega and joint drops (Manny, 1 Nov 2026): one real pack variant per 1, 2 or 3 bottles like taurine, so
  // the drawer shows bottles and a step swaps the pack. `catVarPacks` (by handle, from the snippet) holds each pack's
  // market price, refill price and plan; `catVarGift` the calming 3-pack's free gift.
  const cvp = data.catVarPacks || {};
  const cvOf = l => Object.values(cvp).find(ps => Object.values(ps).some(p => p.id === Number(l.variant_id))) || null;
  const isCatVar = l => !isGift(l) && !!cvOf(l);
  const cvCount = l => l.quantity * (Number(Object.entries(cvOf(l) || {}).find(([, p]) => p.id === Number(l.variant_id))?.[0]) || 1);
  // CALMING_ORPHANS_20261102: only this product's marked, same-group extras qualify.
  const calmingExtra = l => l.properties?._oc_catvar_guide === 'calming' || l.properties?._oc_catvar_gift === 'calming';
  function calmingExtraChanges(c) {
    const updates = {}, kept = new Set();
    const packs = cvp['calming-drops-for-cats'] || {};
    for (const extra of c.items.filter(calmingExtra)) {
      const groupId = extra.properties?._oc_catvar_group;
      const kind = extra.properties?._oc_catvar_guide === 'calming' ? 'guide' : 'gift';
      const threshold = kind === 'guide' ? 2 : 3;
      const qualifies = groupId && c.items.some(main => main.properties?._oc_catvar_group === groupId
        && main.quantity > 0 && main.final_line_price > 0
        && Object.entries(packs).some(([q,p]) => p.id === Number(main.variant_id) && Number(q) >= threshold));
      const identity = groupId + ':' + kind;
      if (!qualifies || extra.selling_plan_allocation || extra.final_line_price > 0 || kept.has(identity)) updates[extra.key] = 0;
      else { kept.add(identity); if (extra.quantity !== 1) updates[extra.key] = 1; }
    }
    return updates;
  }
  async function syncCalmingExtras(c) {
    const updates = calmingExtraChanges(c);
    return Object.keys(updates).length ? request('/cart/update.js', {updates}) : c;
  }
  function catVarItems(line, jars) {
    const p = cvOf(line)?.[jars], subscribe = !!line.selling_plan_allocation;
    if (!p || (subscribe && !(p.plan > 0))) return null;
    const key = crypto.randomUUID(), gift = (data.catVarGift || {})[line.handle];
    const props = Object.fromEntries(Object.entries(line.properties || {}).filter(([k]) => !['__kaching_bundles', '_oc_pack_group'].includes(k)));
    const items = [{id: p.id, quantity: 1, ...(subscribe ? {selling_plan: p.plan} : {}), properties: {...props, _oc_catvar_group: key, Bottles: String(jars), ...(gift?.guideId ? {_oc_digital_gift:jars>=2?'The 20-Year Cat Guide':'none'} : {})}}];
    // CALMING_GUIDE_20261102: preserve the guide through pack edits; no recurring gift.
    if (gift?.guideId && jars >= 2) items.push({id:gift.guideId,quantity:1,properties:{_oc_catvar_group:key,_oc_catvar_guide:gift.product,Edition:data.country==='US'?'US':'International','Download your guide':data.country==='US'?'https://cdn.shopify.com/s/files/1/0968/5450/7811/files/The-20-Year-Cat-OcuLabs.pdf?v=1790969056' : 'https://cdn.shopify.com/s/files/1/0968/5450/7811/files/The-20-Year-Cat-OcuLabs-Intl.pdf?v=1790969059'}});
    if (gift && jars >= gift.at) items.push({id: gift.id, quantity: 1, properties: {_oc_catvar_group: key, _oc_catvar_gift: gift.product}});
    return {items, key};
  }
  const taurineDeal=()=>data.packDeals.find(d=>d.style==='taurine'&&d.market===data.marketId)||data.packDeals.find(d=>d.style==='taurine'&&!d.market);
  function taurineItems(jars,subscribe,baseProps={}){
    const p=tp[jars];if(!p||!(p.sub>0))return null;
    const group=crypto.randomUUID();
    const props=Object.fromEntries(Object.entries(baseProps).filter(([k])=>!['__kaching_bundles','_oc_taurine_group','_oc_pack_group'].includes(k)));
    const items=[{id:p.id,quantity:1,...(subscribe?{selling_plan:p.plan}:{}),properties:{...props,_oc_taurine_group:group,_oc_taurine_pack:String(jars),Bottles:String(jars),'Bottle size':'60 mL',_oc_source_sku:'CJFT317077601AZ',_oc_digital_gift:jars>=2?'The 20-Year Cat Guide':'none'}}];
    if(jars===3)items.push({id:data.taurineGiftId,quantity:1,properties:{_oc_taurine_group:group,_oc_taurine_gift:'cat-care'}});
    return {items,key:group};
  }
  const dealOf = l => {
    if(isTaurine(l))return taurineDeal();
    const k = kaching(l);
    if (!k.main || k.bxgy) return null;
    return (data.packDeals || []).find(d => d.nanoId === k.deal && d.variantId === l.variant_id
      && (!d.versionId || k.v === d.versionId)) || null;
  };
  const steps = l => (isPack(l) && canSwap(l)) || !!dealOf(l) || isCatQty(l) || isDentalQty(l) || isCatVar(l);
  const nanoId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(4)),
    b => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-'[b & 63]).join('');
  // A deal pack in Kaching's own line format (as its widgets and the Hip page write them, 28 Oct 2026): Calming's one
  // line with a `bid` token; Hip's paid line `{id, bar}`, its free jars `{bxgy}` and its guide `{gift}`, sharing one
  // random `id`. Answers with the lines and the new pack's key.
  function dealItems(d, jars, line, base) {
    const bar = d.bars[jars];
    if (!bar) return null;
    // A deal whose bars name their own plan (Vision Resilia's) moves a subscription onto that bar's plan.
    const id = nanoId(), k = kaching(line), plan = line.selling_plan_allocation ? d.plans?.[jars] || bar.plan || line.selling_plan_allocation.selling_plan.id : undefined;
    // Taurine, as its page writes it (28 Oct 2026): the paid line {deal, pp, main, market (AU only), v, id, bid} with our
    // group and the guide note; at 3 bottles the gift line {id, deal, v, gift}.
    if (d.style === 'taurine') return taurineItems(jars,!!line.selling_plan_allocation,base);

    const token = d.style === 'bar' ? {deal: d.nanoId, pp: d.productId, main: true, id, bar: bar.id}
      : {deal: d.nanoId, pp: d.productId, main: true, v: d.versionId, bid: bar.id};
    if (plan && k.sellingPlan) token.sellingPlan = {...k.sellingPlan, id: plan};
    const props = {...base};
    if (d.style === 'bar') { if (props._oc_hip_group) props._oc_hip_group = id; }
    else props._oc_pack_group = id;   // Calming's token has no id, so the pack gets our group
    const withPlan = item => plan ? {...item, selling_plan: plan} : item;
    const items = [withPlan({id: d.variantId, quantity: jars, properties: {...props, __kaching_bundles: JSON.stringify(token)}})];
    if (bar.get) items.push(withPlan({id: d.variantId, quantity: bar.get,
      properties: {...props, __kaching_bundles: JSON.stringify({id, deal: d.nanoId, bar: bar.id, main: true, bxgy: true})}}));
    if (bar.gift) items.push({id: d.giftVariantId, quantity: 1,
      properties: {...(props._oc_hip_group ? {_oc_hip_group: id} : {}), __kaching_bundles: JSON.stringify({id, deal: d.nanoId, gift: bar.gift})}});
    return {items, key: id};
  }
  // Kaching's Bundle Savings takes a pack down to its bar's amount (shop currency at the market rate, less the plan's
  // percentage) when that is below the jars at their own price. Hip's amounts are higher, so its paid jars keep their
  // price and only the free jars go to $0 (checked against real carts, 28 Oct 2026).
  function dealPrice(d, line, jars) {
    if(d.style==='taurine')return tp[jars]?.[line.selling_plan_allocation?'sub':'once']??null;
    const bar = d.bars[jars];
    if (!bar) return null;
    const own = line.original_price * jars;
    // A deal in a fixed currency (taurine's AU deal is in AUD) applies its amounts as they are, in that currency only.
    const r = d.fixedCurrency ? (d.fixedCurrency === data.currency ? 1 : 0) : rate;
    if (!bar.value || !(r > 0)) return own;
    const off = line.selling_plan_allocation ? (kaching(line).sellingPlan?.priceAdjustment?.value || 0) / 100 : 0;
    return Math.min(own, Math.round(Math.round(bar.value * r * 100) * (1 - off)));
  }
  // Premium Cat Probiotic steps 1, 3 and 6 bottles (his ask, 28 Oct 2026: "the qauinty steepr should be for all
  // products"). 3 and 6 bottles are the 1-bottle variant under the market's Kaching quantity deal, as the cat pages add
  // them (oc-cat-qty.js); a pack variant from before that change steps onto the same lines, and 1 bottle is the plain
  // variant on its own plans. A subscription keeps its daily amount: the plan with the same mL moves with the bottles.
  // The guide note comes from 3 bottles and the B12 Surprise Cat-Care Gift line at 6, as on the pages.
  const cq = data.catQty;
  const catSteps = [1, 3, 6];
  const catDeal = l => cq?.deals.find(d => d.nanoId === kaching(l).deal) || null;
  const isCatQty = l => !!cq && !isGift(l) && (l.variant_id === cq.variantId ? !kaching(l).deal || !!catDeal(l)
    : cq.packs[l.variant_id] > 0 && l.quantity === 1);
  const catCount = l => l.variant_id === cq.variantId ? l.quantity : cq.packs[l.variant_id];
  const catNext = (l, step) => step > 0 ? catSteps.find(n => n > catCount(l)) || catCount(l)
    : [...catSteps].reverse().find(n => n < catCount(l)) || 0;
  const catMl = l => {
    const plan = l.selling_plan_allocation?.selling_plan?.id;
    for (const list of Object.values(cq.plans)) if (list.includes(plan)) return list.indexOf(plan) + 1;
    return Number(l.properties?._oc_daily_ml) || 1;
  };
  const catBar = (l, bottles) => {
    const d = catDeal(l) || cq.deals.find(d => d.market === data.marketId) || cq.deals.find(d => !d.market);
    const bar = d?.bars[bottles]?.[l.selling_plan_allocation ? 'sub' : 'once'];
    return bar ? {d, bar} : null;
  };
  function catItems(line, bottles) {
    const plan = line.selling_plan_allocation ? cq.plans[bottles]?.[catMl(line) - 1] : null;
    if (line.selling_plan_allocation && !plan) return null;
    const drop = ['__kaching_bundles', '_oc_pack_group', '_oc_hw_gift_group', '_oc_gifts', '_oc_guide_edition'];
    const props = Object.fromEntries(Object.entries(line.properties || {}).filter(([k]) => !drop.includes(k)));
    const id = nanoId(), withPlan = item => plan ? {...item, selling_plan: plan} : item;
    if (bottles === 1) return {items: [withPlan({id: cq.variantId, quantity: 1, properties: {...props, _oc_pack_group: id}})], key: id};
    const found = catBar(line, bottles);
    if (!found) return null;
    const {d, bar} = found;
    const token = {deal: d.nanoId, pp: cq.productId, main: true, v: d.versionId, id, bar: bar.id,
      ...(plan ? {sellingPlan: {id: plan, priceAdjustment: {type: 'percentage', value: 20}}} : {})};
    // The gift note each page family writes: the newer layouts name the surprise gift and the guide's edition, the older
    // layouts (oc-cat-native.js, through oc-cat-pdp2.js) the monthly Cat Care Line calls.
    const older = !props._oc_hw_page && !/native/.test(props._oc_ks_page || props._oc_bl_page || '');
    props._oc_gifts = '20-Year Cat guide' + (bottles < 6 ? '' : older ? ' + monthly Cat Care Line calls' : ' + Surprise Cat-Care Gift');
    if (!older) props._oc_guide_edition = data.country === 'US' ? 'US' : 'International';
    const items = [withPlan({id: cq.variantId, quantity: bottles, properties: {...props, __kaching_bundles: JSON.stringify(token)}})];
    const tags = Object.fromEntries(Object.entries(props).filter(([k]) => /^_oc_(hw|ks|bl)_page$|^_oc_market$/.test(k)));
    if (bar.gift) items.push({id: cq.giftVariantId, quantity: 1, parent_id: cq.variantId, properties: {...tags,
      _oc_hw_gift: 'surprise-vitamin-b12', __kaching_bundles: JSON.stringify({id, deal: d.nanoId, v: d.versionId, gift: bar.gift})}});
    return {items, key: id};
  }
  // The new line as Shopify will price it: 1 bottle at its own price or plan, a pack at its bar (fixed local amount, or
  // USD at the market rate), the subscribe bars before their plans' 20%.
  function catPrice(line, bottles) {
    if (bottles === 1) {
      const plan = line.selling_plan_allocation ? cq.plans[1][catMl(line) - 1] : null;
      return plan ? cq.singlePlans[plan] ?? null : cq.singlePrice;
    }
    const found = catBar(line, bottles);
    const r = found?.d.fixedCurrency ? (found.d.fixedCurrency === data.currency ? 1 : 0) : rate;
    if (!found || !(r > 0)) return null;
    const once = Math.round(found.bar.value * r * 100);
    return line.selling_plan_allocation ? Math.round(once * 0.8) : once;
  }
  // Cat Dental Topper, Margin and Save offers: 1, 2 and 3 tubs (his ask, 28 Oct 2026: "do the Cat Dental Topper the same
  // way (real quantities plus the stepper)"). 2 and 3 tubs are the offer's 1-tub variant under the market's Kaching deal,
  // as the dental pages now add them (oc-cat-qty.js); a pack variant from before steps onto the same lines, and 1 tub is
  // the plain variant. Each count gets the lines the dental page gives it (`dentalQty` in the snippet): the guide line
  // from 2 tubs on Margin and from 1 on Save, Margin's surprise gift at 3, and on a subscription one tub per 30 days.
  const dq = data.dentalQty;
  const dentalOffer = l => dq && !isGift(l) && l.properties?._oc_dental_request ? dq.offers[l.properties._oc_dental_offer] || null : null;
  const dentalDeal = l => dq?.deals.find(d => d.nanoId === kaching(l).deal) || null;
  const isDentalQty = l => {
    const o = dentalOffer(l);
    return !!o && (l.variant_id === o.single ? !kaching(l).deal || !!dentalDeal(l) : o.packs[l.variant_id] > 0 && l.quantity === 1);
  };
  const dentalCount = l => { const o = dentalOffer(l); return l.variant_id === o.single ? l.quantity : o.packs[l.variant_id]; };
  const dentalBar = (l, tubs) => {
    const o = dentalOffer(l), d = dentalDeal(l) || dq.deals.find(d => d.market === data.marketId) || dq.deals.find(d => !d.market);
    const bar = d?.bars[l.selling_plan_allocation ? 'sub' : 'once'][tubs === 3 ? o.three : String(tubs)];
    return bar ? {d, bar} : null;
  };
  function dentalItems(line, tubs) {
    const o = dentalOffer(line), sub = !!line.selling_plan_allocation;
    const plan = sub ? (tubs === 1 ? o.plan : dq.plans[tubs]) : null;
    if (sub && !plan) return null;
    const drop = ['__kaching_bundles', '_oc_gifts', '_oc_guide_edition'];
    const props = Object.fromEntries(Object.entries(line.properties || {}).filter(([k]) => !drop.includes(k)));
    const request = window.crypto?.randomUUID?.() || Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    const edition = data.country === 'US' ? 'US' : 'International';
    const guide = tubs >= o.guideFrom, surprise = !!o.giftAt && tubs >= o.giftAt;
    // The page's own properties: the request that groups the pack with its gifts, the tubs, and the gift note.
    Object.assign(props, {_oc_dental_request: request, _oc_dental_pack_size: String(tubs), 'Pack contents': `${tubs} × 100g ${tubs === 1 ? 'tub' : 'tubs'}`});
    if (guide) Object.assign(props, {_oc_gifts: '20-Year Cat guide' + (surprise ? ' + Surprise Cat-Care Gift' : ''), _oc_guide_edition: edition});
    const tags = {_oc_dental_request: request, _oc_dental_audience: props._oc_dental_audience, _oc_dental_offer: props._oc_dental_offer, _oc_dental_role: 'gift'};
    const withPlan = item => plan ? {...item, selling_plan: plan} : item;
    const items = [];
    let gift = null;
    if (tubs === 1) items.push(withPlan({id: o.single, quantity: 1, properties: props}));
    else {
      const found = dentalBar(line, tubs);
      if (!found) return null;
      const {d, bar} = found, id = nanoId();
      const token = {deal: d.nanoId, pp: dq.productId, main: true, v: d.versionId, id, bar: bar.id,
        ...(plan ? {sellingPlan: {id: plan, priceAdjustment: {type: 'percentage', value: 20}}} : {})};
      items.push(withPlan({id: o.single, quantity: tubs, properties: {...props, __kaching_bundles: JSON.stringify(token)}}));
      if (surprise) gift = {id: dq.giftVariantId, quantity: 1, parent_id: o.single, properties: {...tags,
        __kaching_bundles: JSON.stringify({id, deal: d.nanoId, v: d.versionId, gift: d.gifts[sub ? 'sub' : 'once']})}};
    }
    if (guide) items.push({id: dq.guideVariantId, quantity: 1, properties: {...tags, 'Guide download': dq.guide[edition === 'US' ? 'US' : 'international'], _oc_guide_edition: edition}});
    if (gift) items.push(gift);
    return {items, key: request};
  }
  // As Shopify will price it: 1 tub at its own price or plan, 2 or 3 at the bar (fixed local amount, or USD at the market
  // rate for the US deal), the subscribe bars before their plans' 20%.
  function dentalPrice(line, tubs) {
    const o = dentalOffer(line), sub = !!line.selling_plan_allocation;
    if (tubs === 1) return (sub ? dq.singlePlans[o.plan] : dq.singlePrice[o.single]) || null;
    const found = dentalBar(line, tubs);
    const r = found?.d.fixedCurrency ? (found.d.fixedCurrency === data.currency ? 1 : 0) : rate;
    if (!found || !(r > 0)) return null;
    const once = Math.round(found.bar.value * r * 100);
    return sub ? Math.round(once * 0.8) : once;
  }
  const packs = () => cart.items.filter(isPack);
  const visionJars = () => cart.items.filter(isVision).reduce((n, l) => n + l.quantity, 0);
  // Every product outside the Vision shipping profile ships free; Vision ships free from 2 jars (US$60, 28 Oct 2026).
  const isParasite = l => Number(l.product_id) === 10430303928611;
  const parasitePage = () => !!document.querySelector('#oc-parasite-product');
  const parasiteCount = () => cart.items.filter(l => !giftMarked(l) && l.final_line_price > 0 && l.requires_shipping !== false).reduce((n,l) => n + (l.product_id === 10430303928611 ? l.quantity * (Number(l.properties?._oc_parasite_bottles)||Number((/^(\d+)/.exec(l.variant_title||'')||[])[1])||1) : bottles(l)),0);
  const freeShipping = () => parasitePage() ? parasiteCount() >= 2 : !cart.items.some(isVision) || visionJars() >= data.shippingFrom;
  // Items in the count: products, including a bundle's free jars, but not free gift products; multi-bottle cat packs
  // count each bottle, as the theme drawer did.
  const bottles = l => l.quantity * (Number((/^(\d+)\s*(?:bottles?|tubs?)\b/i.exec(l.variant_title || '') || [])[1]) || 1);
  const itemCount = () => cart.items.filter(l => !giftMarked(l) && l.original_line_price > 0).reduce((n, l) => n + (isCatVar(l) ? cvCount(l) : bottles(l)), 0);

  // Cat probiotic packs (Manny, 28 Oct 2026: the Vision cart's look "for our cat products"). A pack is a variant of 1, 3
  // or 6 bottles, and the cat pages' gift ladder follows the bottles: `catPacks` in the snippet.
  // An entry with `variants` covers only those packs (the dental offers give different gifts).
  const catPackOf = l => (!isGift(l) && (data.catPacks || []).find(p => p.handle === l.handle
    && (!p.variants || p.variants.includes(l.variant_id)))) || null;
  const catLines = () => cart.items.filter(catPackOf);
  const catBottles = () => catLines().reduce((n, l) => n + bottles(l), 0);
  // The pages show each gift's value in shop currency (USD) at the market rate.
  const giftValue = g => g?.value && rate > 0 ? Math.round(g.value * rate) : 0;
  // A gift with a cart line is found by its marker property (probiotic) or its variant (dental's guide and surprise).
  const giftLine = g => !!(g.line || g.variantId);
  const isLineFor = (g, l) => (g.line && l.properties?.[g.line]) || (g.variantId && l.variant_id === g.variantId);
  const catLineGift = l => {
    for (const p of data.catPacks || []) { const g = p.gifts.find(g => isLineFor(g, l)); if (g) return g; }
    return null;
  };
  function catCard(g) {
    const was = giftValue(g);
    return `<div class="ocv-card"><img src="${esc(g.image)}" alt="" width="64" height="64" loading="lazy"><div>
      <p class="ocv-card__title">Your Free Gift</p><p class="ocv-card__sub">${esc(g.title)}</p>
      <div class="ocv-card__row"><span class="ocv-card__price">${was ? `<s>${money(was)}</s>` : ''}Free</span>
      <span class="ocv-check is-on" aria-hidden="true">Claimed <span class="ocv-check__box">${icons.tick}</span></span></div></div></div>`;
  }

  // The cat pack's tiers, one segment each: free shipping from 1 bottle, the guide from 3, the surprise gift at 6.
  function catProgress(p) {
    const n = catBottles(), tiers = p.gifts;
    const got = tiers.filter(g => n >= g.min), next = tiers.find(g => n < g.min);
    const list = names => names.length > 1 ? names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1] : names[0];
    const msg = !next ? `<b>It’s all yours!</b> ${list(got.map(g => g.short))}`
      : `<b>Nice!</b> ${list(got.map(g => g.short))} unlocked. Choose <b>${next.min} ${p.unit}s</b> for the ${next.short}`;
    return `<div class="ocv-progress"><p class="ocv-progress__msg">${msg}</p>
      <ol class="ocv-progress__steps" style="grid-template-columns:repeat(${tiers.length},1fr)">${tiers.map((g, i) => `<li class="${n >= g.min ? 'on' : ''}">
        <span class="ocv-progress__seg"><i data-ocv-seg="${n >= g.min ? 1 : 0}" style="transform:scaleX(${lastJars > i ? 1 : 0})"></i></span>
        <span class="ocv-progress__label">${n >= g.min ? icons.tick : ''}${esc(g.step)}</span></li>`).join('')}</ol></div>`;
  }

  function progress() {
    if(parasitePage()) {
      const n=parasiteCount(),unlocked=n>=2;
      return `<div class="ocv-progress"><p class="ocv-progress__msg">${unlocked?'<b>Nice!</b> FREE Shipping unlocked':'Add <b>1 more item</b> to unlock FREE Shipping'}</p><ol class="ocv-progress__steps" style="grid-template-columns:repeat(2,1fr)">${[1,2].map((step,i)=>`<li class="${n>=step?'on':''}"><span class="ocv-progress__seg"><i data-ocv-seg="${n>=step?1:0}" style="transform:scaleX(${n>=step?1:0})"></i></span><span class="ocv-progress__label">${i?'Free shipping':'1 item'}</span></li>`).join('')}</ol><p class="ocv-progress__label">Bottles and paid add-ons count.</p></div>`;
    }
    const cat = !cart.items.some(isVision) && catLines()[0];
    if (cat) return catProgress(catPackOf(cat));
    // Vision in the cart: its jar tiers. Anything else already ships free, so the bar says so.
    if (!cart.items.some(isVision)) {
      return `<div class="ocv-progress"><p class="ocv-progress__msg"><b>Nice!</b> Your order ships <b>FREE</b></p>
        <ol class="ocv-progress__steps ocv-progress__steps--one"><li class="on"><span class="ocv-progress__seg"><i data-ocv-seg="1" style="transform:scaleX(${lastJars ? 1 : 0})"></i></span>
        <span class="ocv-progress__label">${icons.tick}Free shipping unlocked</span></li></ol></div>`;
    }
    // Only the Vision cat-layout page adds the guide and Mystery Gift lines (its Kaching deal has no gifts; seen on the
    // original page, 28 Oct 2026), so elsewhere the bar promises only what the cart gives: free shipping from 2 jars.
    const gifts = onVisionPage();
    const jars = Math.min(visionJars(), gifts ? 3 : 2);
    const msg = !gifts ? (jars >= 2 ? '<b>Nice!</b> Your order ships <b>FREE</b>' : 'You’re <b>1 jar away</b> from FREE Shipping')
      // 29 Oct 2026: the cat-layout page's gifts are the Resilia set: guide from 1 jar, water bottle from 2, bundle from 3.
      : window.ocVision?.giftSet === 'original'
        ? (jars >= 3 ? '<b>It’s all yours!</b> FREE Shipping, Free Guide & Mystery Gift'
          : jars === 2 ? '<b>Nice!</b> FREE Shipping & Free Guide unlocked. <b>1 more jar</b> for the Mystery Gift'
          : 'You’re <b>1 jar away</b> from FREE Shipping & a Free Guide')
      : jars >= 3 ? '<b>It’s all yours!</b> FREE Shipping, Guide, Water Bottle & Wellness Bundle'
      : jars === 2 ? '<b>Nice!</b> FREE Shipping & Water Bottle unlocked. <b>1 more jar</b> for the Wellness Dog Bundle'
      : 'You’re <b>1 jar away</b> from FREE Shipping & a Water Bottle';
    // One segment per tier with its label centred under it, so each label sits under the part of the bar it unlocks.
    const steps = !gifts ? ['1 jar', 'Free shipping'] : window.ocVision?.giftSet === 'original' ? ['1 jar', 'Free shipping + guide', 'Mystery gift'] : ['Free guide', 'Free shipping + bottle', 'Wellness bundle'];
    return `<div class="ocv-progress"><p class="ocv-progress__msg">${msg}</p>
      <ol class="ocv-progress__steps" style="grid-template-columns:repeat(${steps.length},1fr)">${steps.map((label, i) => `<li class="${jars > i ? 'on' : ''}">
        <span class="ocv-progress__seg"><i data-ocv-seg="${jars > i ? 1 : 0}" style="transform:scaleX(${lastJars > i ? 1 : 0})"></i></span>
        <span class="ocv-progress__label">${jars > i ? icons.tick : ''}${label}</span></li>`).join('')}</ol></div>`;
  }

  function lineRow(l) {
    const pack = isPack(l);
    const deal = data.deals.find(d => d.variantId === l.variant_id);
    const img = pack ? data.packs[Math.min(l.quantity, 3) - 1] : deal ? deal.image
      : (l.image ? l.image + (l.image.includes('?') ? '&' : '?') + 'width=200' : '');
    const planCompare = l.selling_plan_allocation?.compare_at_price;   // a subscription's one-time price
    const was = isVision(l) && data.visionCompareAt ? data.visionCompareAt * l.quantity
      : deal ? Math.max(deal.compareAt, deal.price) * l.quantity
      : planCompare ? Math.max(planCompare * l.quantity, l.original_line_price) : l.original_line_price;
    const swappable = steps(l);
    const n = isTaurine(l) ? taurineCount(l) : isCatVar(l) ? cvCount(l) : isCatQty(l) ? catCount(l) : isDentalQty(l) ? dentalCount(l) : l.quantity;
    const max = isCatQty(l) ? 6 : swappable ? 3 : 99;
    // A cat pack of 3 or 6 bottles is one variant: a second pack would cost more than the next pack up and miss its
    // gift, so it shows its bottles, its plan, and is removed whole, as the theme drawer did.
    const catPack = catPackOf(l) && bottles(l) > l.quantity;
    // A probiotic pack is now 1 bottle x3 or x6 on a plan named "Deliver every 90 days · 3-bottle pack"; the line already
    // says the bottles, so the plan's pack tail is dropped here (28 Oct 2026). Dental's plans end "· 2-tub pack".
    const plan = (l.selling_plan_allocation?.selling_plan?.name || 'One-time purchase').replace(/ · \d+-(?:bottle|tub) pack$/, '');
    const sub = isParasite(l) ? `<p class="ocv-line__sub">${bottles(l)} bottle${bottles(l)===1?'':'s'} · ${esc(plan)}</p>`
      : catPackOf(l) ? `<p class="ocv-line__sub">${bottles(l)} ${catPackOf(l).unit}${bottles(l) === 1 ? '' : 's'} · ${esc(plan)}</p>`
      : isTaurine(l)?`<p class="ocv-line__sub">${l.selling_plan_allocation?'Subscribe &amp; Save · ':''}${esc(plan)}</p>`
      : isCatVar(l) ? `<p class="ocv-line__sub">${cvCount(l)} bottle${cvCount(l) === 1 ? '' : 's'} · ${esc(plan)}</p>` : '';
    // Any other Kaching bundle (Resilia's, Turkey Tail's) has its quantity set by its deal, so it is removed whole.
    const fixed = isParasite(l) || (catPack && !swappable) || (!!l.properties?.__kaching_bundles && !swappable);
    const qty = fixed
      ? `<div class="ocv-qty ocv-qty--fixed" role="group" aria-label="${esc(l.product_title)} bundle">
          <button type="button" data-ocv-qty="remove" aria-label="Remove ${esc(l.product_title)} bundle">${icons.trash}</button>
          <output>${isParasite(l) || catPack ? bottles(l) : l.quantity}</output></div>`
      : `<div class="ocv-qty" role="group" aria-label="Quantity for ${esc(l.product_title)}">
          <button type="button" data-ocv-qty="-1" aria-label="${n === 1 ? 'Remove' : 'Decrease quantity'}">${n === 1 ? icons.trash : icons.minus}</button>
          <output>${n}</output>
          <button type="button" data-ocv-qty="1" aria-label="Increase quantity"${n >= max ? ' disabled' : ''}>${icons.plus}</button>
        </div>`;
    return `<div class="ocv-line${seen.has(l.key) ? '' : ' is-new'}" data-key="${esc(l.key)}" data-ocv-selling-plan="${esc(l.selling_plan_allocation?.selling_plan?.id || '')}">
      <img class="ocv-line__img" src="${esc(img)}" alt="" width="76" height="76" loading="lazy">
      <div><p class="ocv-line__title">${esc(l.product_title)}</p>${sub}${qty}</div>
      <div class="ocv-line__price">${was > l.final_line_price ? `<s class="ocv-was">${money(was)}</s>` : ''}<span class="ocv-now">${money(l.final_line_price)}</span></div></div>`;
  }

  function giftCards() {
    const gifts = cart.items.filter(isGift);
    // Free shipping rides on the first gift card as an added perk once the cart ships free (his ask, 27 Oct 2026).
    let shipping = freeShipping();
    // A cat pack's gifts with no cart line come first, as on the cat pages: free shipping, then the guide from 3 bottles.
    // Shipping has its own card there, so no other card carries it as a perk.
    const cat = catLines()[0];
    let html = cat ? catPackOf(cat).gifts.filter(g => !giftLine(g) && catBottles() >= g.min).map(catCard).join('') : '';
    if (cat) shipping = false;
    html += gifts.map(l => {
      const own = l.image ? l.image + (l.image.includes('?') ? '&' : '?') + 'width=160' : '';
      // The surprise gift is a real product at $0; its card keeps the surprise (its name and photo stay hidden).
      const surprise = catLineGift(l);
      const fg = freeGiftOf(l);
      const g = data.gifts[l.properties?._oc_vision_gift] || surprise
        || {title: fg?.title || (isFreeUnit(l) ? `${l.product_title} (${l.quantity} free)` : l.product_title),
            image: data.visionVariantIds.includes(l.variant_id) ? data.packs[0] : own};
      const was = surprise ? giftValue(surprise) || l.original_line_price : fg?.value || l.original_line_price;
      const perk = shipping ? `<span class="ocv-perk">${icons.truck}+ FREE Shipping</span>` : '';
      shipping = false;
      return `<div class="ocv-card"><img src="${esc(g.image)}" alt="" width="64" height="64" loading="lazy"><div>
        <p class="ocv-card__title">Your Free Gift${perk ? 's' : ''}</p><p class="ocv-card__sub">${esc(g.title)}</p>${perk}
        <div class="ocv-card__row"><span class="ocv-card__price">${was ? `<s>${money(was)}</s>` : ''}Free</span>
        <span class="ocv-check is-on" aria-hidden="true">Claimed <span class="ocv-check__box">${icons.tick}</span></span></div></div></div>`;
    }).join('');
    // Digital gifts have no cart line: the taurine page gives the 20-Year Cat Guide from 2 bottles, and its old cart
    // offered the PDF there (the Taurine session found it missing under this cart, 28 Oct 2026). US gets its own edition.
    for (const d of data.digitalGifts || []) {
      const bottles = cart.items.filter(l => (d.variantId===55689173008675?isTaurine(l):l.variant_id === d.variantId && !isGift(l))).reduce((n,l)=>n+(d.variantId===55689173008675?taurineCount(l):l.quantity),0);
      if (bottles < d.min) continue;
      const perk = shipping ? `<span class="ocv-perk">${icons.truck}+ FREE Shipping</span>` : '';
      shipping = false;
      html += `<div class="ocv-card"><img src="${esc(d.image)}" alt="" width="64" height="64" loading="lazy"><div>
        <p class="ocv-card__title">Your Free Gift${perk ? 's' : ''}</p><p class="ocv-card__sub">${esc(d.title)}</p>${perk}
        <div class="ocv-card__row"><span class="ocv-card__price">Free</span>
        <a class="ocv-dl" href="${esc(data.country === 'US' ? d.us : d.intl)}" target="_blank" rel="noopener">Download PDF</a></div></div></div>`;
    }
    // The upgrade card promises the cat-layout page's gifts, so it shows on that page only.
    const target = onVisionPage() && packs().find(l => l.quantity < 3);
    if (target) {
      const next = target.quantity + 1;
      // 29 Oct 2026: the cat-layout page's gifts are the Resilia set (guide, water bottle, wellness bundle).
      const og = window.ocVision?.giftSet === 'original';
      const unlock = og ? (next === 2 ? data.gifts['cloudy-eye-guide-og'] : data.gifts['mystery-gift']) : next === 2 ? data.gifts['water-bottle'] : data.gifts['wellness-bundle'];
      const what = og ? (next === 2 ? 'FREE Shipping + Guide' : 'Mystery Gift') : next === 2 ? 'FREE Shipping + Water Bottle' : 'Wellness Dog Bundle';
      // The card shows the pack it upgrades to (his ask, 27 Oct 2026), not the gift, which already has its own card.
      html += `<div class="ocv-card ocv-card--upgrade"><span class="ocv-chip">${icons.bolt}<span>Deal Unlocked: ${what}</span></span>
        <img src="${esc(data.packs[next - 1])}" alt="" width="64" height="64" loading="lazy"><div>
        <p class="ocv-card__title">Upgrade to ${next} jars</p><p class="ocv-card__sub">${esc(unlock.note)}</p>
        <div class="ocv-card__row"><span></span>
        <button type="button" class="ocv-check" data-ocv-upgrade="${esc(target.key)}" aria-label="Claim deal: upgrade to ${next} jars">Claim Deal <span class="ocv-check__box">${icons.tick}</span></button></div></div></div>`;
    }
    return html;
  }

  function deals() {
    const inCart = new Set(cart.items.map(l => l.variant_id));
    // A product already in the cart is not offered again in another size (the cat cart offered the 1-bottle probiotic
    // under a 3-bottle pack, 28 Oct 2026).
    const owned = new Set(cart.items.filter(l => !isGift(l)).map(l => l.handle));
    // A cart of cat products, or an empty cart on a cat page, sees only cat add-ons (his ask, 28 Oct 2026: "for the cat
    // upsells in cart, make it all cat up sells"). A cart of both sees the whole cat range first, then the dog ones. A
    // dog cart sees the dog ones first, then the cat range's two lead add-ons; the rest (`catOnly`) is for cat carts.
    const isCatDeal = d => /(^|-)cat(-|$)/.test(d.handle);
    const paid = cart.items.filter(l => !isGift(l));
    const catLine = l => /(^|-)cat(-|$)/.test(l.handle || '') || !!cvp[l.handle];
    const catCart = paid.length ? paid.every(catLine) : /(^|[\/-])cats?([\/-]|$)/.test(location.pathname);
    const mixed = paid.some(catLine) && !paid.every(catLine);
    const list = data.deals.filter(d => !(cart.items.some(isTaurine) && d.handle === 'cat-l-lysine-immune-capsules') && !inCart.has(d.variantId) && !owned.has(d.handle) && (catCart ? isCatDeal(d) : mixed || !d.catOnly))
      .sort((a, b) => mixed ? isCatDeal(b) - isCatDeal(a) : isCatDeal(a) - isCatDeal(b));
    if (!list.length) return '';
    return `<div class="ocv-deals"><h3>Exclusive Deals</h3><p>Our best sellers at our best prices. Subscriptions skip or cancel anytime.</p>
      ${list.map(d => {
        const info = data.dealInfo[d.handle] || {benefits: []};
        const was = Math.max(d.compareAt, d.price), now = d.planId ? d.subPrice : d.price;
        const off = was > now ? Math.floor((1 - now / was) * 100) : 0;
        const save = d.planId ? Math.floor((1 - d.subPrice / d.price) * 100 + 1e-9) : 0;
        const open = expanded.has(d.variantId);
        // Dr. Sarah Miller's tab shows only where the store already has her words for that product.
        const tabs = [info.vet ? 0 : null, 1, 2].filter(i => i !== null), first = tabs[0];
        // Expanded, PetLab style: key benefits, then Vet Reviewed, Key Ingredients and Directions For Use tabs, then the product page link.
        return `<div class="ocv-deal${open ? ' is-expanded' : ''}"><div class="ocv-deal__top"><div class="ocv-deal__media">${off ? `<span class="ocv-deal__badge">${off}% off</span>` : ''}
          <img src="${esc(d.image)}" alt="" width="110" height="110" loading="lazy"></div>
          <div><p class="ocv-deal__title">${esc(d.title)}</p><p class="ocv-deal__copy">${esc(info.copy)}</p>
          <span class="ocv-card__price">${off ? `<s>${money(was)}</s>` : ''}${money(now)}</span>
          <p class="ocv-deal__terms">${d.planId ? `Subscribe &amp; save ${save}%. Skip or cancel anytime.` : 'One-time purchase'}</p></div></div>
          <div class="ocv-deal__actions"><button type="button" class="ocv-deal__add" data-ocv-add="${d.variantId}" data-ocv-plan="${d.planId || ''}">${icons.cart}Add to Cart</button>
          <button type="button" class="ocv-deal__more" data-ocv-more="${d.variantId}" aria-expanded="${open}" aria-label="${open ? 'Hide' : 'Show'} details for ${esc(d.title)}">${icons.chevron}</button></div>
          <div class="ocv-deal__extra"><div><div class="ocv-deal__info">
            <p class="ocv-deal__h">Key Benefits</p>
            <ul class="ocv-deal__benefits">${info.benefits.map(b => `<li>${icons.checkCircle}<span>${esc(b)}</span></li>`).join('')}</ul>
            <div class="ocv-tabs" role="tablist" aria-label="${esc(d.title)} details">
              ${tabs.map(i => `<button type="button" role="tab" aria-selected="${i === first}" data-ocv-tab="${i}">${['Vet Reviewed', 'Key Ingredients', 'Directions For Use'][i]}</button>`).join('')}</div>
            ${info.vet ? `<div class="ocv-tabs__panel" role="tabpanel" data-ocv-panel="0">
              <p class="ocv-vet__quote">“${esc(info.vet)}”</p>
              <div class="ocv-vet"><img src="${esc(data.vet.image)}" alt="" width="64" height="64" loading="lazy">
                <div><p class="ocv-vet__name">${esc(data.vet.name)}</p><p class="ocv-vet__title">${esc(data.vet.title)}</p></div></div></div>` : ''}
            <div class="ocv-tabs__panel" role="tabpanel" data-ocv-panel="1"${first === 1 ? '' : ' hidden'}>
              <ul class="ocv-ings">${(info.ingredients || []).map(g => `<li${data.ingImages[g.img] ? '' : ' class="ocv-ings--text"'}>
                ${data.ingImages[g.img] ? `<img src="${esc(data.ingImages[g.img])}" alt="" width="56" height="56" loading="lazy">` : ''}
                <div><p class="ocv-ings__name">${esc(g.name)}${g.amount ? ` <span>${esc(g.amount)}</span>` : ''}</p>
                ${g.desc ? `<p class="ocv-ings__desc">${esc(g.desc)}</p>` : ''}</div></li>`).join('')}</ul>
              ${info.ingredientsNote ? `<p class="ocv-ings__note">${esc(info.ingredientsNote)}</p>` : ''}</div>
            <div class="ocv-tabs__panel" role="tabpanel" data-ocv-panel="2" hidden>${esc(info.howTo)}</div>
            <a class="ocv-deal__link" href="${esc(d.url)}">View product details →</a></div></div></div></div>`;
      }).join('')}</div>`;
  }

  // Keep the theme's header cart bubble in step, the way the theme drawer refreshed it after each change.
  let headerRequest = 0;
  async function syncHeader(n) {
    if (n === headerCount || !document.getElementById('cart-icon-bubble')) return;
    headerCount = n;
    const mine = ++headerRequest;     // a slower, older answer must not overwrite a newer one
    try {
      const json = await fetch(`${base}/?sections=cart-icon-bubble`, {cache: 'no-store'}).then(r => r.json());
      const fresh = new DOMParser().parseFromString(json['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
      const bubble = document.getElementById('cart-icon-bubble');
      if (fresh && bubble && mine === headerRequest) bubble.innerHTML = fresh.innerHTML;
    } catch {}
  }

  function render() {
    if (!cart) return;
    if (cart.items.some(isTaurine) || cart.items.some(isCatVar)) taurineCartSeen = true;
    const scroll = body.scrollTop;
    const lines = cart.items.filter(l => !isGift(l));
    const n = itemCount();
    count.textContent = n;
    count.parentElement.lastChild.textContent = n === 1 ? ' item)' : ' items)';
    total.textContent = money(cart.total_price);
    checkout.setAttribute('aria-disabled', String(!cart.item_count || checkingOut));
    body.innerHTML = `<div class="ocv-cart__inner">${lines.length ? progress() + lines.map(lineRow).join('') + giftCards()
      : `<div class="ocv-empty"><div class="ocv-empty__icon">${icons.bag}</div><p>Your cart is empty</p></div>`}${deals()}</div>`;
    body.scrollTop = scroll;
    cart.items.forEach(l => seen.add(l.key));
    // Segments start where the last render left them, then fill or empty in order.
    const segs = [...body.querySelectorAll('[data-ocv-seg]')];
    const reached = segs.filter(s => s.dataset.ocvSeg === '1').length;
    if (segs.length) {
      void body.offsetWidth;
      const rising = reached >= lastJars;
      segs.forEach((seg, i) => {
        seg.style.transitionDelay = (rising ? Math.max(0, i - lastJars) : Math.max(0, lastJars - 1 - i)) * 0.14 + 's';
        seg.style.transform = `scaleX(${seg.dataset.ocvSeg})`;
      });
      lastJars = reached;
    } else lastJars = 0;
    window.ocVision?.cartCount?.(cart);
    document.querySelectorAll('[data-hw-cart-count], [data-testid="navigation-cart-trigger"] span').forEach(el => { el.textContent = String(cart.item_count); });
    syncHeader(cart.item_count);
  }

  // A gift must never be charged: once its line stops being free (its paid item went), it leaves the cart.
  async function pruneGifts() {
    for (let guard = 0; guard < 6; guard++) {
      const index = cart.items.findIndex(l => (giftMarked(l) && l.final_line_price > 0) || (l.properties?._oc_taurine_gift==='cat-care'&&!cart.items.some(m=>isTaurine(m)&&taurineCount(m)>=3&&bundleKey(m)===bundleKey(l))));
      if (index < 0) break;
      cart = await request('/cart/change.js', {line: index + 1, quantity: 0});
    }
    cart = await syncCalmingExtras(cart);
    // Then the hidden-page free gifts (1 Nov 2026). A failure here never shows the shopper an error: the cart is read again.
    try { cart = await syncFreeGifts(cart); } catch { try { cart = await loadCart(); } catch {} }
  }

  // Every quantity change shows at once, as PetLab's does (his asks, 28 Oct 2026: "on petlabco, its near instant", then
  // "it doesnt allow to remove 1 at a time" quickly on a Vision pack). A tap updates the local cart and the screen;
  // the asked quantity per line waits in `want` and goes to Shopify in the background, in order, a moment after the
  // last tap; Shopify's answer then replaces the guess, and on an error the real cart comes back with a message.
  let syncing = Promise.resolve(), inFlight = 0, timer = null;
  const want = new Map();     // line id -> quantity asked for and not yet sent
  const alias = new Map();    // a Vision pack's old group -> the group its swap made, so later taps find the new pack
  const lineId = l => bundleKey(l) || l.key;
  const resolve = id => { while (alias.has(id)) id = alias.get(id); return id; };

  // Apply a quantity to the local cart: the line, its price, and the free jars and gifts the new count changes.
  function guess(line, next) {
    const id = lineId(line);
    const swap = isPack(line) && canSwap(line), d = dealOf(line), bar = d?.bars[next], cat = isCatQty(line);
    const o = isDentalQty(line) && dentalOffer(line);
    const items = cart.items.flatMap(l => {
      if (lineId(l) !== id) return [l];
      if (next <= 0) return [];
      if (l !== line) {
        if (cat) return kaching(l).gift && next < 6 ? [] : [l];
        if (o) return l.variant_id === dq.giftVariantId ? (o.giftAt && next >= o.giftAt ? [l] : [])
          : l.variant_id === dq.guideVariantId ? (next >= o.guideFrom ? [l] : []) : [l];
        if (d?.style==='taurine') return next>=3?[l]:[];
        if (isCatVar(line)) {
          const gift = (data.catVarGift || {})[line.handle];
          const threshold = l.properties?._oc_catvar_guide ? 2 : gift?.at || 99;
          return isGift(l) && next < threshold ? [] : [l];
        }
        if (d) return kaching(l).bxgy ? (bar?.get ? [{...l, quantity: bar.get}] : []) : kaching(l).gift && !bar?.gift ? [] : [l];
        const gift = data.gifts[l.properties?._oc_vision_gift];
        return swap && gift && next < gift.min ? [] : [l];
      }
      if (cat) {
        const price = catPrice(l, next) ?? Math.round(l.final_line_price / catCount(l) * next), spa = l.selling_plan_allocation;
        return [{...l, variant_id: cq.variantId, variant_title: '1 bottle', quantity: next, final_line_price: price,
          original_line_price: Math.max(cq.singlePrice * next, price), selling_plan_allocation: spa && {...spa, compare_at_price: cq.singlePrice,
            selling_plan: {...spa.selling_plan, name: `Deliver every ${next * 30 / catMl(l)} days`}}}];
      }
      if (o) {
        const price = dentalPrice(l, next) ?? Math.round(l.final_line_price / dentalCount(l) * next), spa = l.selling_plan_allocation;
        const one = dq.singlePrice[o.single] || 0;
        return [{...l, variant_id: o.single, variant_title: '1 tub', quantity: next, final_line_price: price,
          original_line_price: Math.max(one * next, price), selling_plan_allocation: spa && {...spa, compare_at_price: one,
            selling_plan: {...spa.selling_plan, name: `Deliver every ${next * 30} days`}}}];
      }
      if (isCatVar(l)) {
        const p = cvOf(l)[next], spa = l.selling_plan_allocation, price = p[spa ? 'sub' : 'once'];
        return [{...l, variant_id: p.id, variant_title: `${next} Bottles`, quantity: 1, original_line_price: p.once, final_line_price: price,
          selling_plan_allocation: spa && {...spa, compare_at_price: p.once, selling_plan: {...spa.selling_plan, id: p.plan, name: `Refill every ${next * 60} days`}}}];
      }
      if(isTaurine(l)){
        const p=tp[next],spa=l.selling_plan_allocation,price=p[spa?'sub':'once'];
        return [{...l,variant_id:p.id,variant_title:`${next} Bottles`,quantity:1,original_line_price:p.once,final_line_price:price,selling_plan_allocation:spa&&{...spa,compare_at_price:p.once,selling_plan:{...spa.selling_plan,id:p.plan,name:`Refill every ${next*60} days`}}}];
      }
      const exact = swap ? builder().price?.(next, !!l.selling_plan_allocation) : d ? dealPrice(d, l, next) : null;
      const unit = l.final_line_price / l.quantity, unitWas = l.original_line_price / l.quantity;
      const price = exact ?? Math.round(unit * next);
      const spa=l.selling_plan_allocation;
      return [{...l, quantity: next, final_line_price: price, original_line_price: Math.max(Math.round(unitWas * next), price),
        ...(d?.style==='taurine'&&spa?{selling_plan_allocation:{...spa,selling_plan:{...spa.selling_plan,id:d.plans?.[next]||spa.selling_plan.id,name:`Refill every ${next*60} days`}}}:{})}];
    });
    const sum = list => list.reduce((n, l) => n + l.final_line_price, 0);
    cart = {...cart, items, item_count: items.reduce((n, l) => n + l.quantity, 0),
      total_price: Math.max(0, cart.total_price + sum(items) - sum(cart.items))};
    render();
  }

  function change(line, next) {
    if (checkingOut) return;
    checkoutError = null;
    guess(line, next);
    status.textContent = next <= 0 ? 'Removed from your cart' : 'Cart updated';
    want.set(lineId(line), Math.max(0, next));
    clearTimeout(timer);
    timer = setTimeout(kick, 350);     // a run of taps on one line becomes one change on Shopify
  }
  function kick() {
    clearTimeout(timer); timer = null;
    if (!want.size) return;
    inFlight++;
    syncing = syncing.then(flush).then(async server => {
      if (--inFlight === 0 && !timer && !want.size) { cart = server; await pruneGifts(); render(); }
    }).catch(async error => {
      checkoutError = error;
      inFlight = Math.max(0, inFlight - 1); want.clear();
      status.textContent = error.message;
      try { cart = await loadCart(); render(); } catch {}
    });
  }
  // Send every asked quantity, against a fresh cart, and answer with Shopify's cart.
  async function flush() {
    let server = await loadCart();
    for (const [asked, quantity] of [...want]) {
      want.delete(asked);
      const id = resolve(asked);
      const members = server.items.filter(l => lineId(l) === id);
      const main = members.find(l => !isGift(l)) || members[0];
      if (!main) continue;                                    // already gone
      if (quantity === 0) server = await removeLines(server, members);
      else if ((isTaurine(main) ? taurineCount(main) : isCatVar(main) ? cvCount(main) : isCatQty(main) ? catCount(main) : isDentalQty(main) ? dentalCount(main) : main.quantity) === quantity) continue;
      else if (steps(main)) {
        const swapped = await swapPack(main, quantity);
        alias.set(id, swapped.group);
        server = swapped.cart;
      }
      else server = await setQuantity(main, quantity);
    }
    return server;
  }
  // One update.js removes a whole bundle; a key it no longer finds is finished by position.
  async function removeLines(server, lines) {
    const keys = lines.map(l => l.key);
    let fresh;
    try { fresh = await request('/cart/update.js', {updates: Object.fromEntries(keys.map(k => [k, 0]))}); }
    catch { fresh = server; }
    const left = l => keys.includes(l.key) || lines.some(m => bundleKey(m) && bundleKey(m) === bundleKey(l));
    return fresh.items.some(left) ? removeWhere(left) : fresh;
  }

  // A task may return the cart JSON that change.js and update.js answer with; only when it doesn't is the cart read
  // again (each extra GET /cart.js cost 0.25 to 0.6 s, measured 28 Oct 2026). Queued quantity changes go first.
  async function run(task, message) {
    if (busy || checkingOut) return;
    checkoutError = null;
    busy = true; root.classList.add('is-busy'); status.textContent = '';
    try {
      if (timer) kick();
      await syncing;
      const result = await task();
      cart = isCart(result) ? result : await loadCart();
      await pruneGifts(); render(); if (message) status.textContent = message;
    }
    catch (error) { checkoutError = error; status.textContent = error.message; try { cart = await loadCart(); render(); } catch {} }
    finally { busy = false; root.classList.remove('is-busy'); }
  }

  checkout.addEventListener('click', async event => {
    if (!taurineCartSeen) return;
    event.preventDefault();
    if (checkingOut) return;
    checkingOut = true;
    let leaving = false;
    checkout.setAttribute('aria-disabled', 'true');
    checkout.setAttribute('aria-busy', 'true');
    root.classList.add('is-busy');
    status.textContent = 'Confirming your cart…';
    try {
      const taurineAdd = window.OcuLabsTaurineCommerce?.pending;
      if (taurineAdd) await taurineAdd;
      const deadline = Date.now() + 20000;
      do {
        if (Date.now() > deadline) throw new Error('Your cart is still updating. Please try checkout again.');
        if (timer) kick();
        await syncing;
        if (busy) await wait(50);
      } while (timer || inFlight || want.size || busy);
      if (checkoutError) throw checkoutError;
      cart = await loadCart();
      for(const l of [...cart.items.filter(isTaurine)]){
        const q=taurineCount(l),p=tp[q];
        if(!p||q>3)throw new Error('Please choose a Taurine supply of 1, 2 or 3 bottles.');
        const oldTaurineGift=q===3&&cart.items.some(g=>g.properties?._oc_taurine_gift==='cat-care'&&g.variant_id!==data.taurineGiftId&&bundleKey(g)===bundleKey(l));
        if(l.quantity!==1||l.variant_id!==p.id||(l.selling_plan_allocation&&l.selling_plan_allocation.selling_plan.id!==p.plan)||oldTaurineGift)cart=(await swapPack(l,q)).cart;
      }
      await pruneGifts();
      if (!cart.item_count) throw new Error('Your cart is empty. Add an item before checking out.');
      render();
      location.assign(checkout.href);
      leaving = true;
    } catch (error) {
      status.textContent = error.message;
      try { cart = await loadCart(); render(); } catch {}
    } finally {
      if (!leaving) {
        checkingOut = false;
        checkout.removeAttribute('aria-busy');
        checkout.setAttribute('aria-disabled', String(!cart?.item_count));
        if (!busy) root.classList.remove('is-busy');
      }
    }
  });

  // Remove the lines that match, one at a time, by their position in a freshly read cart, and answer with the cart
  // that is left. The fallback when a key has gone stale: adding a pack makes Kaching rewrite the other lines of the
  // same deal, which changes their keys (seen 27 Oct 2026).
  async function removeWhere(match) {
    let fresh = await loadCart();
    for (let guard = 0; guard < 12; guard++) {
      const index = fresh.items.findIndex(match);
      if (index < 0) return fresh;
      fresh = await request('/cart/change.js', {line: index + 1, quantity: 0});
    }
    throw new Error('Your cart needs a quick refresh. Please reload the page.');
  }
  // Swap a pack (Vision, Calming or Hip) for the same pack mode with a new jar count: add the new pack first, then take
  // the old pack and its free jars and gifts out in one update.js by position in the cart read after the add (keys
  // change on the add), so a failed add never leaves the shopper with nothing. Answers with the cart and the new key.
  async function swapPack(line, jars) {
    const props = Object.fromEntries(Object.entries(line.properties || {}).filter(([k]) => !['__kaching_bundles', '_oc_vision_group', '_oc_pack_group'].includes(k)));
    const d = dealOf(line), cat = isCatQty(line) || isDentalQty(line);
    const taurineItems=d?.style==='taurine'&&window.OcuLabsTaurineBundle?await window.OcuLabsTaurineBundle.items(jars,!!line.selling_plan_allocation):null;
    const cv = isCatVar(line);
    const built = cv ? catVarItems(line, jars) : taurineItems?{items:taurineItems,key:bundleKey(taurineItems[0])}:isCatQty(line) ? catItems(line, jars) : cat ? dentalItems(line, jars) : d ? dealItems(d, jars, line, props) : null;
    const items = cat || d || cv ? built?.items : builder().bundleItems(jars, !!line.selling_plan_allocation, props);
    if (!items) throw new Error('This supply is unavailable right now.');
    const oldGroup = bundleKey(line), newGroup = cat || d || cv ? built.key : items[0].properties._oc_vision_group;
    // A pack with no key (the original pages' Vision and Calming) is found by what a rewritten key keeps: variant,
    // plan and jars.
    const plan = l => l.selling_plan_allocation?.selling_plan?.id || 0;
    const old = l => oldGroup ? bundleKey(l) === oldGroup : !bundleKey(l) && (l.key === line.key
      || (l.variant_id === line.variant_id && plan(l) === plan(line) && l.quantity === line.quantity));
    await request('/cart/add.js', {items});
    // The cart now holds both packs, so a failed read or removal is tried again before the error shows.
    for (let attempt = 1; ; attempt++) {
      try {
        const fresh = await loadCart();
        let out;
        try { out = await request('/cart/update.js', {updates: fresh.items.map(l => old(l) ? 0 : l.quantity)}); }
        catch { out = fresh; }
        if (out.items.some(old)) out = await removeWhere(old);
        return {cart: out, group: newGroup};
      } catch (error) {
        if (attempt === 3) throw error;
        await wait(1500 * attempt);
      }
    }
  }
  // Change a quantity by the line's key, which is current in the cart flush() just read; if Kaching rewrote it, find
  // the line by position in a fresh cart instead.
  async function setQuantity(line, quantity) {
    try { return await request('/cart/change.js', {id: line.key, quantity}); }
    catch {
      const fresh = await loadCart();
      const index = fresh.items.findIndex(l => l.key === line.key);
      if (index < 0) throw new Error('Your cart changed. Please try again.');
      return request('/cart/change.js', {line: index + 1, quantity});
    }
  }

  body.addEventListener('click', event => {
    if (checkingOut) { event.preventDefault(); return; }
    const qty = event.target.closest('[data-ocv-qty]');
    if (qty) {
      const row = qty.closest('[data-key]');
      const line = cart.items.find(l => l.key === row.dataset.key);
      const next = qty.dataset.ocvQty === 'remove' ? 0 : isCatQty(line) ? catNext(line, Number(qty.dataset.ocvQty))
        : isDentalQty(line) ? Math.min(3, dentalCount(line) + Number(qty.dataset.ocvQty))
        : isTaurine(line)?taurineCount(line)+Number(qty.dataset.ocvQty)
        : isCatVar(line) ? Math.min(3, cvCount(line) + Number(qty.dataset.ocvQty)) : line.quantity + Number(qty.dataset.ocvQty);
      return change(line, next);
    }
    const up = event.target.closest('[data-ocv-upgrade]');
    if (up) {
      up.classList.add('is-on');
      const line = cart.items.find(l => l.key === up.dataset.ocvUpgrade);
      return setTimeout(() => { change(line, line.quantity + 1); status.textContent = 'Deal claimed'; }, 280);
    }
    const add = event.target.closest('[data-ocv-add]');
    if (add) {
      add.innerHTML = 'Adding…';
      const item = {id: Number(add.dataset.ocvAdd), quantity: 1};
      if (add.dataset.ocvPlan) item.selling_plan = Number(add.dataset.ocvPlan);
      return run(() => request('/cart/add.js', {items: [item]}), 'Added to your cart');
    }
    const more = event.target.closest('[data-ocv-more]');
    if (more) {
      const card = more.closest('.ocv-deal');
      const id = Number(more.dataset.ocvMore);
      const open = card.classList.toggle('is-expanded');
      if (open) expanded.add(id); else expanded.delete(id);
      more.setAttribute('aria-expanded', String(open));
      more.setAttribute('aria-label', more.getAttribute('aria-label').replace(/^(Show|Hide)/, open ? 'Hide' : 'Show'));
      return;
    }
    const tab = event.target.closest('[data-ocv-tab]');
    if (tab) {
      const info = tab.closest('.ocv-deal__info');
      info.querySelectorAll('[data-ocv-tab]').forEach(t => t.setAttribute('aria-selected', String(t === tab)));
      info.querySelectorAll('[data-ocv-panel]').forEach(p => { p.hidden = p.dataset.ocvPanel !== tab.dataset.ocvTab; });
    }
  });
  body.addEventListener('scroll', () => head.classList.toggle('is-shadow', body.scrollTop > 4), {passive: true});

  async function open(next) {
    const taurine = window.OcuLabsTaurineCommerce;
    if (taurine?.enabled) {
      try {
        do {
          if (taurine.pending) await taurine.pending;
          next = await loadCart();
        } while (taurine.pending); // An add can begin while an earlier cart read is still in flight.
      } catch (error) { status.textContent = error.message; return; }
    }
    if (isCart(next)) cart = next; else next = null;
    if (!isOpen) {
      isOpen = true; lastFocus = document.activeElement;
      root.hidden = false;
      document.documentElement.classList.add('ocv-cart-lock');
      if (!cart) body.innerHTML = '';
      root.classList.add('is-fresh');
      setTimeout(() => root.classList.remove('is-fresh'), 900);
      void panel.offsetHeight;          // start the slide from the closed position without waiting for a frame
      root.classList.add('is-open');
      setTimeout(() => panel.focus({preventScroll: true}), 60);
    }
    if (!next) {
      if (timer || inFlight) { render(); return; }      // taps still on their way: keep showing them, not a stale read
      try { cart = await loadCart(); await pruneGifts(); } catch (error) { status.textContent = error.message; return; }
    }
    render();
  }
  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    panel.style.transform = '';
    const done = () => { if (!isOpen) { root.hidden = true; document.documentElement.classList.remove('ocv-cart-lock'); } };
    panel.addEventListener('transitionend', done, {once: true});
    setTimeout(done, 650);
    lastFocus?.focus?.({preventScroll: true});
  }
  root.querySelectorAll('[data-ocv-close]').forEach(el => el.addEventListener('click', close));
  document.addEventListener('keydown', event => {
    if (!isOpen) return;
    if (event.key === 'Escape') close();
    if (event.key === 'Tab') {
      const items = [...panel.querySelectorAll('a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter(el => el.offsetParent);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  // Drag the sheet down to close on phones, like Vaul.
  let drag = null;
  head.addEventListener('pointerdown', event => {
    if (event.target.closest('button') || matchMedia('(min-width:750px)').matches) return;
    drag = {y: event.clientY, t: performance.now(), dy: 0};
    head.setPointerCapture(event.pointerId);
    root.classList.add('is-dragging');
  });
  head.addEventListener('pointermove', event => {
    if (!drag) return;
    drag.dy = Math.max(0, event.clientY - drag.y);
    panel.style.transform = `translate3d(0,${drag.dy}px,0)`;
  });
  const endDrag = () => {
    if (!drag) return;
    const speed = drag.dy / Math.max(1, performance.now() - drag.t);
    root.classList.remove('is-dragging');
    panel.style.transform = '';
    if (drag.dy > 120 || speed > 0.6) close();
    drag = null;
  };
  head.addEventListener('pointerup', endDrag);
  head.addEventListener('pointercancel', endDrag);

  // Take over the theme's drawer. Theme product forms call renderContents() after an add, apps and GemPages call open()
  // or add its "active" class, and the header icon calls open(); each of those now opens this drawer instead.
  function takeOver(Drawer) {
    if (!Drawer || Drawer.prototype.__ocCart) return;
    Drawer.prototype.__ocCart = true;
    Drawer.prototype.open = function () { open(); };
    Drawer.prototype.renderContents = function () { open(); };
  }
  if (window.customElements) customElements.whenDefined('cart-drawer').then(() => takeOver(customElements.get('cart-drawer')));
  const watchShrine = () => document.querySelectorAll('cart-drawer').forEach(el => {
    if (el.__ocWatched) return;
    el.__ocWatched = true;
    new MutationObserver(() => {
      if (!el.classList.contains('active')) return;
      el.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      open();
    }).observe(el, {attributes: true, attributeFilter: ['class']});
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchShrine, {once: true}); else watchShrine();
  // PetLab-clone pages (Probiotic Chews, 28 Oct 2026) build their own cart dialog in #clone-modal-root after an add or
  // a header click. Close it through its own backdrop, which also lifts the page's inert state, and open this one.
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.id !== 'clone-modal-root' || !/^Your Cart\b/i.test(node.querySelector('h2')?.textContent.trim() || '')) continue;
      node.style.visibility = 'hidden';
      node.querySelector('[data-action="close"]')?.click();
      open();
    }
  }).observe(document.body, {childList: true});
  // The header cart icon and cart links open the drawer instead of the cart page (except on the cart page itself).
  document.addEventListener('click', event => {
    const link = event.target.closest('#cart-icon-bubble, a[href="/cart"], a[href$="/cart"]');
    if (!link || root.contains(link) || /^\/cart\/?$/.test(location.pathname) || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    event.stopPropagation();
    open();
  }, true);

  // Hidden-page free gifts on page load, when the server saw the cart out of step (`dirty`): for example a gift line
  // left from before the 1 Nov correction, or one removed on the cart page. The cart page shows the server's cart, so it
  // reloads once after a change.
  if (freeGifts.some(g => g.dirty)) {
    loadCart().then(async before => {
      const after = await syncFreeGifts(before);
      if (after === before) return;
      cart = after; syncHeader(after.item_count);
      if (isOpen) render();
      if (!/\/cart\/?$/.test(location.pathname)) return;
      let last = 0;      // one reload a minute at most, so a cart that will not settle never loops
      try { last = Number(sessionStorage.getItem('ocFreeGiftReload')) || 0; sessionStorage.setItem('ocFreeGiftReload', String(Date.now())); } catch {}
      if (Date.now() - last > 60000) location.reload();
    }).catch(() => {});
  }

  // Native full-cart edits are owned by the theme. Reconcile marked calming extras after its rows update.
  if (/^\/cart\/?$/.test(location.pathname)) {
    const nativeItems = document.querySelector('cart-items');
    if (nativeItems) {
      // CALMING_NATIVE_QUANTITY_20261102: own only the calming full-cart controls.
      const calmingIds = Object.values(cvp['calming-drops-for-cats'] || {}).map(p => p.id);
      let quantityPending = false;
      const calmingInput = element => {
        const input = element?.matches?.('input[data-quantity-variant-id]') ? element
          : element?.closest('quantity-input')?.querySelector('input[data-quantity-variant-id]');
        return input && calmingIds.includes(Number(input.dataset.quantityVariantId)) ? input : null;
      };
      const setNativeQuantity = async (input, quantity) => {
        if (quantityPending || !Number.isInteger(quantity) || quantity < 1) return;
        const oldValue = input.dataset.cartQuantity || input.defaultValue;
        if (quantity === Number(oldValue)) return;
        quantityPending = true;
        const controls = [...nativeItems.querySelectorAll('input,button'), ...document.querySelectorAll('[name="checkout"]')];
        const disabledBefore = controls.map(control => control.disabled);
        controls.forEach(control => { control.disabled = true; });
        input.value = oldValue;
        nativeItems.setAttribute('aria-busy', 'true');
        try {
          const before = await loadCart();
          const remove = input.closest('tr')?.querySelector('a[href*="/cart/change?"]');
          const key = remove ? new URL(remove.href, location.href).searchParams.get('id') : null;
          const matches = before.items.filter(line => line.variant_id === Number(input.dataset.quantityVariantId));
          const line = before.items.find(line => line.key === key) || (matches.length === 1 ? matches[0] : null);
          if (!line) throw new Error('Your cart changed. Please refresh it before changing quantity.');
          const after = await request('/cart/change.js', {id:line.key,quantity});
          await syncCalmingExtras(after);
          location.reload();
        } catch (error) {
          const errors = document.getElementById('cart-errors');
          if (errors) errors.textContent = error.message || 'Please refresh your cart and try again.';
          controls.forEach((control,index) => { control.disabled = disabledBefore[index]; });
          nativeItems.removeAttribute('aria-busy');
          quantityPending = false;
        }
      };
      nativeItems.addEventListener('click', event => {
        const button = event.target.closest('button[name="plus"],button[name="minus"]');
        const input = calmingInput(button);
        if (!input) return;
        event.preventDefault(); event.stopImmediatePropagation();
        setNativeQuantity(input, Math.max(1, Number(input.value) + (button.name === 'plus' ? 1 : -1)));
      }, true);
      nativeItems.addEventListener('change', event => {
        const input = calmingInput(event.target);
        if (!input) return;
        event.stopImmediatePropagation();
        setNativeQuantity(input, Number(input.value));
      }, true);
      let timer, running = false, again = false;
      const reconcile = async () => {
        if (running) { again = true; return; }
        running = true;
        try {
          const before = await loadCart(), after = await syncCalmingExtras(before);
          if (after !== before) { location.reload(); return; }
        } catch (error) {
          const errors = document.getElementById('cart-errors');
          if (errors) errors.textContent = 'Please refresh your cart to confirm your free gifts.';
        } finally { running = false; if (again) { again = false; schedule(); } }
      };
      const schedule = () => { clearTimeout(timer); timer = setTimeout(reconcile, 200); };
      new MutationObserver(schedule).observe(nativeItems, {childList:true, subtree:true});
      schedule();
    }
  }

  window.ocCart = {open, close, refresh: () => open()};
  window.ocVisionCart = window.ocCart;   // the Vision cat-layout buy box calls this name
})();

